import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Zap, Trophy, Users, Star, CheckCircle } from 'lucide-react';
import {
  HeroMedia,
  FeaturedProperty,
  PropertyGrid,
  Button,
  ShimmerButton,
  useViewport,
} from '@raffle/ui';
import { usePropertiesStore } from '../stores/propertiesStore';
import { toPropertyData } from '../lib/propertyAdapter';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useViewport();

  const { properties, isLoading, error, fetchProperties } = usePropertiesStore();

  useEffect(() => {
    void fetchProperties({ page: 1, status: 'active' });
  }, [fetchProperties]);

  const propertyData = useMemo(() => properties.map(toPropertyData), [properties]);
  const featuredProperty = propertyData[0];
  const activeProperties = propertyData.slice(0, 6);

  const features = [
    {
      icon: Shield,
      title: 'Wahala-Free Draws',
      description: 'No long story! Every draw is transparent with full audit trails. No go-slow here.',
    },
    {
      icon: Zap,
      title: 'Sharp Sharp Tickets',
      description: 'Your ticket lands immediately after payment. No waiting, no stories.',
    },
    {
      icon: Trophy,
      title: 'Real Winners, Real Keys',
      description: 'We don\'t do yanga. Verified winners announced publicly with real property transfers.',
    },
    {
      icon: Users,
      title: 'Your Odds, Your Chance',
      description: 'Buy more tickets, get more chances. Na your matter be that!',
    },
  ];

  const trustBadges = [
    { label: '10,000+', desc: 'Happy Nigerians' },
    { label: '₦5B+', desc: 'Properties Won' },
    { label: '100%', desc: 'Verified Draws' },
    { label: '24/7', desc: 'Support Team' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="relative">
        <HeroMedia
          desktopImage="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&auto=format"
          mobileImage="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&auto=format"
          alt="Luxury Nigerian property"
          overlay
          parallax={isDesktop}
          className={isMobile ? 'h-[85vh]' : 'h-[90vh]'}
        >
          {/* Nigerian Accent Stripe */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#008751]/20 text-[#00B36B] text-sm font-medium mb-6 border border-[#008751]/30">
              <Star className="w-4 h-4" />
              Be your Oga's neighbour
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-text-primary mb-6 leading-tight">
              Own Your
              <br />
              <span className="text-gradient-gold">Own Dream Home</span>
            </h1>
            <p className="text-lg text-text-secondary mb-8 max-w-lg">
              From Lekki to Banana Island, Ikoyi to VI. Enter premium raffles and 
              become a property owner. No loans, no wahala — just your winning ticket!
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <ShimmerButton size="xl" onClick={() => navigate('/properties')}>
                Explore Properties
                <ArrowRight className="w-5 h-5 ml-2" />
              </ShimmerButton>
              <Button
                variant="outline"
                size="xl"
                onClick={() => navigate('/draws')}
                className="border-[#008751]/50 text-[#00B36B] hover:bg-[#008751]/10 hover:border-[#008751]"
              >
                See Past Winners
              </Button>
            </div>
            
            {/* Trust Badges */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-wrap gap-6 mt-10 pt-8 border-t border-white/10"
            >
              {trustBadges.map((badge, i) => (
                <div key={i} className="text-center">
                  <p className="font-mono text-2xl font-bold text-gold-500">{badge.label}</p>
                  <p className="text-xs text-text-muted">{badge.desc}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </HeroMedia>
      </section>

      {/* How It Works - Nigerian Style */}
      <section className="section-spacing bg-surface-1 relative overflow-hidden">
        {/* Adire Pattern Background */}
        <div className="absolute inset-0 adire-pattern opacity-50" />
        
        <div className="content-wrapper relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#008751]/20 text-[#00B36B] text-sm font-medium mb-4">
              E Easy Like ABCD
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
              How <span className="text-gradient-gold">Own Your Own</span> Works
            </h2>
            <p className="text-text-secondary max-w-xl mx-auto">
              Three simple steps to your dream property. No long grammar, no wahala.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Pick Your Property', desc: 'Browse luxury properties across Lagos and choose your dream home.', icon: '🏠' },
              { step: '02', title: 'Buy Your Tickets', desc: 'Get tickets for just ₦1,000 each. The more tickets, the better your chances!', icon: '🎟️' },
              { step: '03', title: 'Win & Collect Keys', desc: 'When you win, we handle all the legal transfer. You just move in!', icon: '🔑' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-6 rounded-vault-lg bg-surface-2/50 border border-white/5 hover:border-gold-500/20 transition-all group"
              >
                <span className="absolute -top-4 left-6 text-6xl opacity-10 font-display font-bold text-gold-500 group-hover:opacity-20 transition-opacity">
                  {item.step}
                </span>
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-display text-lg font-semibold text-text-primary mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-text-secondary">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Features */}
      <section className="section-spacing">
        <div className="content-wrapper">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-6 rounded-vault-lg bg-surface-2/50 border border-white/5 hover:border-[#008751]/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-vault bg-gradient-to-br from-[#008751]/20 to-gold-500/20 flex items-center justify-center mb-4 group-hover:from-[#008751]/30 group-hover:to-gold-500/30 transition-colors">
                  <feature.icon className="w-6 h-6 text-gold-500" />
                </div>
                <h3 className="font-display text-lg font-semibold text-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-text-secondary">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Property */}
      <section className="section-spacing bg-surface-1">
        <div className="content-wrapper">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-gold-500/20 text-gold-500 text-sm font-medium mb-4">
              🔥 Hot Property Alert
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Featured Property
            </h2>
            <p className="text-text-secondary max-w-2xl">
              This one na correct banger! Don't sleep on this opportunity o.
            </p>
          </motion.div>

          {error ? (
            <div className="p-6 rounded-vault-lg bg-surface-2/40 border border-white/5 text-text-secondary">
              Unable to load featured property: {error}
            </div>
          ) : featuredProperty ? (
            <FeaturedProperty
              property={featuredProperty}
              onViewDetails={() => navigate(`/properties/${featuredProperty.id}`)}
              onBuyTicket={() => navigate(`/properties/${featuredProperty.id}`)}
            />
          ) : isLoading ? (
            <div className="p-6 rounded-vault-lg bg-surface-2/40 border border-white/5 text-text-secondary">
              Loading featured property…
            </div>
          ) : (
            <div className="p-6 rounded-vault-lg bg-surface-2/40 border border-white/5 text-text-secondary">
              No active properties yet.
            </div>
          )}
        </div>
      </section>

      {/* Active Raffles */}
      <section className="section-spacing relative overflow-hidden">
        {/* Aso-Oke Pattern */}
        <div className="absolute inset-0 aso-oke-pattern" />
        
        <div className="content-wrapper relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-end justify-between mb-12"
          >
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#008751]/20 text-[#00B36B] text-sm font-medium mb-4">
                Live Raffles
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
                Properties <span className="text-gradient-gold">Dey Wait</span> For You
              </h2>
              <p className="text-text-secondary max-w-2xl">
                Check out these premium Lagos properties. Your next home fit be among them!
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/properties')}
              className="hidden sm:flex border-[#008751]/50 text-[#00B36B] hover:bg-[#008751]/10"
            >
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>

          {error ? (
            <div className="p-6 rounded-vault-lg bg-surface-2/40 border border-white/5 text-text-secondary">
              Unable to load properties: {error}
            </div>
          ) : (
            <PropertyGrid
              properties={activeProperties}
              onPropertyClick={(property) => navigate(`/properties/${property.id}`)}
            />
          )}

          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" onClick={() => navigate('/properties')} className="border-[#008751]/50 text-[#00B36B]">
              View All Properties
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials - Nigerian Style */}
      <section className="section-spacing bg-surface-1">
        <div className="content-wrapper">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-gold-500/20 text-gold-500 text-sm font-medium mb-4">
              Wetin People Dey Talk
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary">
              Real Winners, Real Stories
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Chioma A.', location: 'Won in Lekki', quote: 'I no believe am until dem give me key! OYO changed my life forever. God bless una!', avatar: '👩🏾' },
              { name: 'Emeka O.', location: 'Won in Ikoyi', quote: 'My wife think say na scam when I tell am. Now we dey our own house. Na God o!', avatar: '👨🏾' },
              { name: 'Fatima B.', location: 'Won in VI', quote: 'From ₦5,000 worth of tickets to property owner. If I fit win, you fit win too!', avatar: '👩🏾‍💼' },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-vault-lg bg-surface-2/50 border border-white/5"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{testimonial.avatar}</span>
                  <div>
                    <p className="font-semibold text-text-primary">{testimonial.name}</p>
                    <p className="text-xs text-[#00B36B]">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-text-secondary italic">"{testimonial.quote}"</p>
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing">
        <div className="content-wrapper">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-vault-xl p-8 md:p-12 text-center"
            style={{
              background: 'linear-gradient(135deg, #008751 0%, #006B40 50%, #D4AF37 100%)',
            }}
          >
            {/* Adire Pattern Overlay */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>

            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Become a Property Owner?
              </h2>
              <p className="text-white/90 max-w-xl mx-auto mb-8">
                Join over 10,000 Nigerians already playing. Your winning ticket dey wait for you!
                Make your move today — no be by mouth, na by action.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="xl"
                  className="bg-white text-[#008751] hover:bg-white/90 font-bold"
                  onClick={() => navigate('/register')}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Create Free Account
                </Button>
                <Button
                  variant="outline"
                  size="xl"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => navigate('/properties')}
                >
                  Browse Properties
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 relative">
        {/* Nigerian Flag Stripe */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
        
        <div className="content-wrapper">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-vault bg-gradient-to-br from-[#008751] to-gold-500 flex items-center justify-center shadow-lg shadow-gold-500/20 group-hover:shadow-gold-500/40 transition-shadow">
                <span className="text-white font-bold text-xs">OYO</span>
              </div>
              <div>
                <span className="font-display text-lg font-semibold text-text-primary block leading-none">
                  Own Your <span className="text-gold-500">Own</span>
                </span>
                <span className="text-[10px] text-text-muted">Be your Oga's neighbour</span>
              </div>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-6 text-sm text-text-secondary">
              <a href="#" className="hover:text-text-primary transition-colors">About Us</a>
              <a href="#" className="hover:text-text-primary transition-colors">How E Work</a>
              <a href="#" className="hover:text-text-primary transition-colors">FAQ</a>
              <a href="#" className="hover:text-text-primary transition-colors">Terms</a>
              <a href="#" className="hover:text-text-primary transition-colors">Privacy</a>
              <a href="/support" className="hover:text-text-primary transition-colors">Support</a>
            </nav>
            <p className="text-sm text-text-muted">
              © 2026 Own Your Own. Be your Oga's neighbour 🇳🇬
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
